import { Offer, PriceSpecification, PropertyValue, UnitPriceSpecification } from "schema-dts";
import { Addon, DiscountRow, Variant } from "./OrderForm/types.ts";
import { TFunction } from "i18next";

export const getAddons = (productVariant: Variant, additionalServices: Addon[], t: TFunction<"translation", undefined>) => {

  // const { t } = useAppTranslation();

  const addons = additionalServices.filter(addon => addon.products.some(addonProduct => addonProduct.id === productVariant.hs_id));

  // Group addons by their type.label
  const groupedAddons = addons.reduce<Record<string, Addon[]>>((acc, addon) => {
    const typeLabel = addon.type.label;
    if (!acc[typeLabel]) {
      acc[typeLabel] = [];
    }
    acc[typeLabel].push(addon);
    return acc;
  }, {});

  // Get min and mac price for each addon type
  const addonPrices = Object.entries(groupedAddons).map(([typeLabel, addons]) => {
    const priceList = addons.map(addon => addon.monthly_price ?? addon.energy_price)
    const minPrice = Math.min(...priceList);
    const maxPrice = Math.max(...priceList);
    return {
      typeLabel,
      minPrice,
      maxPrice,
      type: addons[0].monthly_price ? "monthly_price" : "energy_price",
    };
  });

  return addonPrices.map(addon => ({
    "@type": "Offer",
    name: addon.typeLabel,
    description: addon.type === "monthly_price" ? t("ldjson.addonDescription1", {price: addon.minPrice}) : t("ldjson.addonDescription2"),
    priceSpecification: {
      "@type": "UnitPriceSpecification",
      minPrice: addon.minPrice,
      maxPrice: addon.maxPrice,
      priceCurrency: "EUR",
      unitText: addon.type === "monthly_price" ? t("common.€/month") : "€/kWh",
      referenceQuantity: {
        "@type": "QuantitativeValue",
        value: 1,
        unitCode: addon.type === "monthly_price" ? "MON" : "KWH"
      }
    }
  })) as Offer[];
}

export const getAdditionalProperties = (variant: Variant, t: TFunction<"translation", undefined>): PropertyValue[] => {

  const isB2bProduct = variant.vat.label === "0";
  const contractPeriod = variant.contract_period.label;
  const isFixedPeriod = contractPeriod.includes(' kk');
  const period = isFixedPeriod ? contractPeriod.split(' kk')[0] : contractPeriod;

  const defaultProperties: PropertyValue[] = [
    {
      "@type": "PropertyValue",
      name: t("ldjson.contractType"),
      value: isFixedPeriod ? t("ldjson.contractTypeFixed", {period}) : t("ldjson.contractTypeOngoing"),
      description: isFixedPeriod ? t("ldjson.contractTypeFixedDesc", {period}) : t("ldjson.contractTypeOngoingDesc")
    },
    {
      "@type": "PropertyValue",
      name: t("ldjson.app"),
      value: t("ldjson.omalumme"),
      description: t("ldjson.omalummeDesc")
    },
    {
      "@type": "PropertyValue",
      name: t("ldjson.customerPerks"),
      value: t("ldjson.lummeTeam"),
      description: t("ldjson.lummeTeamDesc")
    },
    {
      "@type": "PropertyValue",
      name: t("ldjson.customerType"),
      value: isB2bProduct ? t("ldjson.contractToBusiness") : t("ldjson.contractToConsumer"),
    }
  ]

  const isSpotPriced = variant.name.includes('rssis')
  if (isSpotPriced) {
    const spotPriceProperty: PropertyValue = {
      "@type": "PropertyValue",
      name: t("ldjson.fixedSpotPrice"),
      value: t("ldjson.fixedSpotPriceValue"),
      description: t("ldjson.fixedSpotPriceDesc")
    }
    defaultProperties.push(spotPriceProperty);
  }
  return defaultProperties as PropertyValue[];

}

export const getCleanedText = (text: string) => {
  if (!text) {
    return '';
  }
  // Remove all html tags and extra spaces and non-breaking space character
  return text.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').replace(/&nbsp;/g, ' ').trim();
}

export const getPriceElement = (variant: Variant, discountRows: DiscountRow[], t: TFunction<"translation", undefined>): PriceSpecification => {
  const discountRow = discountRows?.find(row => row.productId === variant.hs_id);
  const discountPeriodSameAsContract = discountRow?.duration === parseInt(variant.contract_period.label.split(' kk')[0]);
  const isSpotPriced = variant.name.includes('rssis');

  const getEnergyPrice = (energyPrice: number) => {
    // energyPrice is cent/kWh, so we convert it to €/kWh
    // eg energyPrice = 2 means 0.02 €/kWh, energyPrice = 8.54 means 0.0854 €/kWh
    return (Math.round(energyPrice * 100) / 10000 + 0.00001).toFixed(4).replace(/\.?0+$/, '');
  }

  const normalMonthlyPrice: UnitPriceSpecification = {
    "@type": "UnitPriceSpecification",
    price: variant.monthly_price,
    priceType: discountRow ? "https://schema.org/StrikethroughPrice" : "https://schema.org/ListPrice",
    priceCurrency: "EUR",
    name: t("common.monthlyPrice"),
    unitText: t("ldjson.month"),
  }

  const energyPriceElement: UnitPriceSpecification = {

    "@type": "UnitPriceSpecification",
    price: getEnergyPrice(variant.energy_price),
    priceCurrency: "EUR",
    name: t("common.energyPrice"),
    unitText: "€/kWh",
    description: isSpotPriced ? `SPOT + ${getEnergyPrice(variant.energy_price)} €/kWh` : `${getEnergyPrice(variant.energy_price)} €/kWh`,
    referenceQuantity: {
      "@type": "QuantitativeValue",
      value: 1,
      unitCode: "KWH"
    }
  }

  const discountPriceElement: UnitPriceSpecification = {
    "@type": "UnitPriceSpecification",
    price: 0, //discountPeriodSameAsContract ? 0 : productVariant.monthly_price,
    priceType: "https://schema.org/SalePrice",
    priceCurrency: "EUR",
    name: t("ldjson.monthlyPriceOffer"),
    billingDuration: discountRow?.duration ?? 1,
    billingIncrement: 1,
    description: discountRow && discountPeriodSameAsContract
      ? `${t("common.monthlyPrice")} 0 €/kk`
      : discountRow && !discountPeriodSameAsContract
        ? t("ldJson.monthlyPriceDiscount", {duration: discountRow.duration, price: variant.monthly_price})
        : `${t("common.monthlyPrice")} ${variant.monthly_price} €/kk`,
  }

  const compoundPrices = [
    energyPriceElement,
    normalMonthlyPrice
  ]

  if (discountRow) {
    compoundPrices.push(discountPriceElement);
  }


  const prices: PriceSpecification = {
    "@type": "CompoundPriceSpecification",
    name: t("ldjson.compoundPriceName"),
    priceCurrency: "EUR",
    price: discountPeriodSameAsContract ? 0 : variant.monthly_price,
    priceComponent: compoundPrices,
  }

  return prices;
}